import React from 'react';

/**
 * Lightweight skeleton placeholder primitive.
 *
 * Uses Tailwind's built-in `animate-pulse` (no extra dependency). Renders a
 * neutral gray block sized/shaped by the caller via `className`. Use it to build
 * content-shaped loading states that mirror the real layout (avoiding layout
 * jump when the real content arrives).
 *
 * @param {Object} props
 * @param {string} [props.className]  - extra Tailwind classes (size, margin, etc.)
 * @param {boolean|string} [props.rounded=true] - true → `rounded`; pass a Tailwind
 *        radius class (e.g. 'rounded-full', 'rounded-2xl') to override; false → none.
 */
const Skeleton = ({ className = '', rounded = true }) => {
  const radius = rounded === true ? 'rounded' : (rounded || '');
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse bg-gray-200 ${radius} ${className}`}
    />
  );
};

/**
 * A stack of skeleton text lines. The last line is shortened for a natural look.
 *
 * @param {Object} props
 * @param {number} [props.lines=3]   - number of lines to render.
 * @param {string} [props.className] - applied to the wrapper (spacing, width, etc.)
 * @param {string} [props.lineClassName] - per-line classes (height, color override).
 */
export const SkeletonText = ({ lines = 3, className = '', lineClassName = 'h-3' }) => (
  <div className={`space-y-2 ${className}`} aria-hidden="true">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={`${lineClassName} ${i === lines - 1 ? 'w-2/3' : 'w-full'}`}
      />
    ))}
  </div>
);

export default Skeleton;
